/**
 * Canvas snap-to-grid preference (enabled + grid size in px).
 *
 * Kept dead-simple on purpose (same philosophy as projectsFlag.ts): a
 * localStorage-backed boolean + number, no React Context. Cross-component
 * reactivity (UserMenu toggle/slider → editor canvas) uses a window
 * CustomEvent, matching the existing 'fileStorageUpdated' pattern.
 *
 * NOTE: settings consolidation is planned — when a unified settings panel
 * lands, fold this into it. For now the controls live in the user-profile menu.
 */
import { useEffect, useState } from 'react';

const ENABLED_KEY = 'canvas_snap_grid';
const SIZE_KEY = 'canvas_snap_grid_size';
const CHANGE_EVENT = 'canvasSnapGridChanged';

export const DEFAULT_SNAP_GRID_SIZE = 32;
export const MIN_SNAP_GRID_SIZE = 32;
export const MAX_SNAP_GRID_SIZE = 256;

export function isSnapToGridEnabled(): boolean {
  try {
    return window.localStorage?.getItem(ENABLED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function getSnapGridSize(): number {
  try {
    const raw = window.localStorage?.getItem(SIZE_KEY);
    const n = raw ? parseInt(raw, 10) : DEFAULT_SNAP_GRID_SIZE;
    if (!Number.isFinite(n)) return DEFAULT_SNAP_GRID_SIZE;
    return Math.min(MAX_SNAP_GRID_SIZE, Math.max(MIN_SNAP_GRID_SIZE, n));
  } catch {
    return DEFAULT_SNAP_GRID_SIZE;
  }
}

function notifyChange(): void {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function setSnapToGridEnabled(enabled: boolean): void {
  try {
    window.localStorage?.setItem(ENABLED_KEY, enabled ? 'true' : 'false');
  } catch {
    // localStorage unavailable (private mode) — the event still updates this session
  }
  notifyChange();
}

export function setSnapGridSize(size: number): void {
  const clamped = Math.min(MAX_SNAP_GRID_SIZE, Math.max(MIN_SNAP_GRID_SIZE, Math.round(size)));
  try {
    window.localStorage?.setItem(SIZE_KEY, String(clamped));
  } catch {
    // localStorage unavailable — event still updates this session
  }
  notifyChange();
}

export interface SnapGridSetting {
  enabled: boolean;
  size: number;
}

/** Reactive hook — re-renders when the toggle or grid size changes anywhere in the app. */
export function useSnapGrid(): SnapGridSetting {
  const [setting, setSetting] = useState<SnapGridSetting>(() => ({
    enabled: isSnapToGridEnabled(),
    size: getSnapGridSize(),
  }));

  useEffect(() => {
    const onChange = () => setSetting({ enabled: isSnapToGridEnabled(), size: getSnapGridSize() });
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);

  return setting;
}
